import React from "react";
import { CommunitySelect } from "./CommunitySelect";
import type { AdminNotificationCommunityOption } from "../api/client";
import type { CampaignFilters as Filters } from "../api/campaigns";
const booleanFilters = [
  ["hasSearches", "Hat Fahrten gesucht"],
  ["hasCreatedRideRequests", "Hat Fahrtanfragen erstellt"],
  ["hasCreatedRides", "Hat Fahrten erstellt"],
  ["hasCompletedRides", "Hat Fahrten abgeschlossen"],
  ["hasCompletedRideRequests", "Hat Fahrtanfragen abgeschlossen"],
  ["diditVerified", "Verifiziert"],
  ["hasVehicle", "Hat Fahrzeug"],
  ["agbAccepted", "AGB akzeptiert"],
  ["hasBio", "Hat Profiltext"],
  ["hasEmergencyContacts", "Hat Notfallkontakte"],
  ["hasReceivedReviews", "Hat Bewertungen erhalten"],
  ["hasGivenReviews", "Hat Bewertungen gegeben"],
  ["emailOptedOut", "E-Mail-Opt-out"],
  ["neverSeen", "Nie aktiv"],
] as const;
const inputClass = "form-control mt-1.5 w-full font-normal";
function localDateTime(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}
export function CampaignFilters({
  value,
  onChange,
  communities,
}: {
  value: Filters;
  communities: AdminNotificationCommunityOption[];
  onChange: (v: Filters) => void;
}) {
  function set(key: string, next: unknown) {
    onChange({ ...value, [key]: next === "" ? undefined : next });
  }
  function renderBoolean([key, label]: (typeof booleanFilters)[number]) {
    return (
      <label key={key} className="text-sm">
        {label}
        <select
          className={inputClass}
          value={value[key] == null ? "any" : String(value[key])}
          onChange={(e) =>
            set(
              key,
              key === "neverSeen"
                ? e.target.value === "any"
                  ? undefined
                  : e.target.value === "true"
                : e.target.value
            )
          }
        >
          <option value="any">Alle</option>
          <option value="true">Ja</option>
          <option value="false">Nein</option>
        </select>
      </label>
    );
  }
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Nutzerfilter kombinieren
        </h3>
        <button
          type="button"
          className="text-xs font-medium text-secondary hover:underline"
          onClick={() => onChange({})}
        >
          Zurücksetzen
        </button>
      </div>
      <p className="text-xs leading-relaxed text-gray-500">
        Alle gesetzten Filter müssen zutreffen. Ohne Filter gilt die gesamte
        gewählte Zielgruppe.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          Name, E-Mail oder ID
          <input
            className={inputClass}
            value={value.query || ""}
            onChange={(e) => set("query", e.target.value)}
          />
        </label>
        <CommunitySelect
          communities={communities}
          value={value.communityId || ""}
          onChange={(id) => set("communityId", id)}
        />
        {booleanFilters.slice(0, 2).map(renderBoolean)}
      </div>
      <details className="group rounded-xl border border-gray-200 bg-gray-50/60">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700">
          Weitere Filter · Aktivität, Profil & Registrierung
        </summary>
        <div className="grid gap-4 border-t border-gray-200 p-4 sm:grid-cols-2">
          {booleanFilters.slice(2).map(renderBoolean)}
          <label className="text-sm">
            Registriert ab
            <input
              className={inputClass}
              type="date"
              value={value.registeredFrom || ""}
              onChange={(e) => set("registeredFrom", e.target.value)}
            />
          </label>
          <label className="text-sm">
            Registriert bis
            <input
              className={inputClass}
              type="date"
              value={value.registeredTo || ""}
              onChange={(e) => set("registeredTo", e.target.value)}
            />
          </label>
          <label className="text-sm">
            Letzte Aktivität vor
            <input
              className={inputClass}
              type="datetime-local"
              value={localDateTime(value.lastSeenBefore)}
              onChange={(e) =>
                set(
                  "lastSeenBefore",
                  e.target.value
                    ? new Date(e.target.value).toISOString()
                    : undefined
                )
              }
            />
          </label>
          <label className="text-sm">
            Inaktiv seit
            <select
              className={inputClass}
              value=""
              onChange={(e) => {
                if (e.target.value)
                  set(
                    "lastSeenBefore",
                    new Date(
                      Date.now() - Number(e.target.value) * 86400000
                    ).toISOString()
                  );
              }}
            >
              <option value="">Zeitraum wählen</option>
              {[7, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {d} Tagen
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Rolle
            <select
              className={inputClass}
              value={value.mobilityRole || ""}
              onChange={(e) => set("mobilityRole", e.target.value)}
            >
              <option value="">Alle</option>
              <option value="DRIVER">Fahrer</option>
              <option value="RIDER">Mitfahrer</option>
              <option value="BOTH">Beides</option>
            </select>
          </label>
          <label className="text-sm">
            Aktiv seit
            <input
              className={inputClass}
              type="datetime-local"
              value={localDateTime(value.lastSeenSince)}
              onChange={(e) =>
                set(
                  "lastSeenSince",
                  e.target.value
                    ? new Date(e.target.value).toISOString()
                    : undefined
                )
              }
            />
          </label>
          <label className="text-sm">
            Gruppen-ID
            <input
              className={inputClass}
              value={value.groupId || ""}
              onChange={(e) => set("groupId", e.target.value)}
            />
          </label>
          <label className="text-sm">
            Mindestens Ø Bewertung
            <input
              className={inputClass}
              type="number"
              min="0"
              max="5"
              step="0.1"
              value={value.minAverageRating ?? ""}
              onChange={(e) =>
                set(
                  "minAverageRating",
                  e.target.value === "" ? undefined : Number(e.target.value)
                )
              }
            />
          </label>
          <label className="text-sm">
            Stripe-Status
            <select
              className={inputClass}
              value={value.stripeConnectStatus || "any"}
              onChange={(e) => set("stripeConnectStatus", e.target.value)}
            >
              {[
                "any",
                "NO_ACCOUNT",
                "ONBOARDING_REQUIRED",
                "IN_REVIEW",
                "PAYOUTS_DISABLED",
                "READY",
              ].map((v) => (
                <option key={v} value={v}>
                  {v === "any" ? "Alle" : v}
                </option>
              ))}
            </select>
          </label>
        </div>
      </details>
    </div>
  );
}
